import axios from '../axios';

import type { Track } from '../interfaces/track';
import type { Playlist, PlaylistItem } from '../interfaces/playlists';
import type { Pagination, PaginationQueryParams } from '../interfaces/api';
import { mapJioSaavnPlaylistToSpotifyPlaylist, mapJioSaavnSongToSpotifyTrack } from '../utils/jiosaavn-adapters';

const CUSTOM_PLAYLISTS_KEY = 'spotify_clone_custom_playlists';

const getCustomPlaylists = (): Playlist[] => {
  try {
    const data = localStorage.getItem(CUSTOM_PLAYLISTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

const saveCustomPlaylists = (playlists: Playlist[]) => {
  localStorage.setItem(CUSTOM_PLAYLISTS_KEY, JSON.stringify(playlists));
};

const getPlaylist = async (playlistId: string) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const found = customPlaylists.find(p => p.id === playlistId);
    if (found) return { data: found };
    throw new Error('Playlist not found');
  }

  const res = await axios.get<any>(`/playlists`, { params: { id: playlistId, limit: 100 } });
  const data = res.data?.data || {};
  return { data: mapJioSaavnPlaylistToSpotifyPlaylist(data) };
};

interface GetPlaylistItemsParams extends PaginationQueryParams { fields?: string; }

const getPlaylistItems = async (playlistId: string, params: GetPlaylistItemsParams = { limit: 1000 }) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const found = customPlaylists.find(p => p.id === playlistId);
    if (!found) throw new Error('Playlist not found');
    
    // (found as any)._tracks contains the actual track objects we'll store
    const tracks = (found as any)._tracks || [];
    
    const items = tracks.map((track: any) => ({
      added_at: new Date().toISOString(),
      added_by: { external_urls: { spotify: '' }, href: '', id: 'user', type: 'user', uri: '' },
      is_local: false,
      track
    }));
    
    return {
      data: {
        href: '',
        items,
        limit: params.limit || 1000,
        offset: params.offset || 0,
        total: items.length,
        previous: null,
        next: null
      } as unknown as Pagination<PlaylistItem>
    };
  }

  const res = await axios.get<any>(`/playlists`, { params: { id: playlistId, limit: 1000 } });
  const data = res.data?.data || {};
  const allTracks = (data.songs || []).map(mapJioSaavnSongToSpotifyTrack);
  const allItems = allTracks.map((track: any) => ({
    added_at: '2023-01-01T00:00:00Z',
    added_by: { external_urls: { spotify: '' }, href: '', id: 'user', type: 'user', uri: '' },
    is_local: false,
    track
  }));

  const limit = params.limit || 1000;
  const offset = params.offset || 0;
  const items = allItems.slice(offset, offset + limit);

  return {
    data: {
      href: '',
      items,
      limit,
      offset,
      total: allItems.length,
      previous: null,
      next: null
    } as unknown as Pagination<PlaylistItem>
  };
};

const getMyPlaylists = async (params: PaginationQueryParams = {}) => {
  const customPlaylists = getCustomPlaylists();
  return { data: { items: customPlaylists, limit: 50, offset: 0, total: customPlaylists.length, previous: null, next: null, href: '' } as unknown as Pagination<Playlist> };
};

interface GetFeaturedPlaylistsParams extends PaginationQueryParams { locale?: string; }

const getFeaturedPlaylists = async (_params: GetFeaturedPlaylistsParams = {}) => {
  return { data: { playlists: { items: [], total: 0, limit: 0, offset: 0, next: null, previous: null, href: '' } as unknown as Pagination<Playlist> } };
};

const addPlaylistItems = async (playlistId: string, uris: string[], snapshot_id: string, tracksData?: any[]) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const foundIndex = customPlaylists.findIndex(p => p.id === playlistId);
    if (foundIndex > -1) {
      const playlist = customPlaylists[foundIndex] as any;
      if (!playlist._tracks) playlist._tracks = [];
      
      // Basic mock: we'll just push a dummy track object with the URI
      // A full implementation would fetch track details from JioSaavn.
      // But just for saving, we'll store basic identifiers.
      uris.forEach((uri, index) => {
        // Only adding if not already there, for simplicity
        if (!playlist._tracks.find((t: any) => t.uri === uri)) {
          const trackData = tracksData?.[index];
          playlist._tracks.push(trackData || {
            id: uri.split(':').pop(),
            uri,
            name: 'Track ' + uri.split(':').pop(),
            artists: [{ name: 'Unknown Artist' }],
            album: { name: 'Unknown Album', images: [] },
            duration_ms: 200000
          });
        }
      });
      playlist.tracks.total = playlist._tracks.length;
      saveCustomPlaylists(customPlaylists);
    }
    return { data: 'OK' };
  }
  return { data: 'OK' };
};

const removePlaylistItems = async (playlistId: string, uris: string[], snapshot_id: string) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const foundIndex = customPlaylists.findIndex(p => p.id === playlistId);
    if (foundIndex > -1) {
      const playlist = customPlaylists[foundIndex] as any;
      if (playlist._tracks) {
        playlist._tracks = playlist._tracks.filter((t: any) => !uris.includes(t.uri));
        playlist.tracks.total = playlist._tracks.length;
        saveCustomPlaylists(customPlaylists);
      }
    }
    return { data: 'OK' };
  }
  return { data: 'OK' };
};

const reorderPlaylistItems = async (playlistId: string, uris: string[], rangeStart: number, insertBefore: number, rangeLength: number, snapshotId: string) => ({ data: 'OK' });
const changePlaylistDetails = async (playlistId: string, data: any) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const found = customPlaylists.find(p => p.id === playlistId);
    if (found) {
      if (data.name) found.name = data.name;
      if (data.description !== undefined) found.description = data.description;
      if (data.public !== undefined) found.public = data.public;
      saveCustomPlaylists(customPlaylists);
    }
    return { data: 'OK' };
  }
  return { data: 'OK' };
};
const changePlaylistImage = async (playlistId: string, image: string, content: string) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const found = customPlaylists.find(p => p.id === playlistId);
    if (found) {
      // Reconstruct base64 data URL
      const dataUrl = `data:${content};base64,${image}`;
      found.images = [{ url: dataUrl, width: 300, height: 300 }];
      saveCustomPlaylists(customPlaylists);
    }
    return { data: 'OK' };
  }
  return { data: 'OK' };
};

const createPlaylist = async (userId: string, data: any) => {
  const customPlaylists = getCustomPlaylists();
  const newPlaylistId = `custom_playlist_${Date.now()}`;
  const newPlaylist: any = {
    id: newPlaylistId,
    name: data.name || `My Playlist #${customPlaylists.length + 1}`,
    description: data.description || '',
    public: true,
    collaborative: false,
    type: 'playlist',
    uri: `spotify:playlist:${newPlaylistId}`,
    href: '',
    external_urls: { spotify: '' },
    images: [],
    owner: { id: userId, display_name: 'You', type: 'user', uri: `spotify:user:${userId}`, href: '', external_urls: { spotify: '' } },
    tracks: { href: '', total: 0 },
    _tracks: [] // custom field to hold tracks locally
  };
  
  customPlaylists.push(newPlaylist);
  saveCustomPlaylists(customPlaylists);
  
  return { data: newPlaylist };
};

const getRecommendations = async (params: any) => ({ data: { tracks: [] as Track[] } });
const getPlaylists = async (_userId: string, params: any) => getMyPlaylists(params);
const deletePlaylist = async (playlistId: string) => {
  if (playlistId.startsWith('custom_playlist_')) {
    const customPlaylists = getCustomPlaylists();
    const updatedPlaylists = customPlaylists.filter((p) => p.id !== playlistId);
    saveCustomPlaylists(updatedPlaylists);
    return { data: 'OK' };
  }
  return { data: 'OK' };
};

export const playlistService = {
  getPlaylist,
  getPlaylists,
  getMyPlaylists,
  createPlaylist,
  getPlaylistItems,
  addPlaylistItems,
  getRecommendations,
  changePlaylistImage,
  removePlaylistItems,
  getFeaturedPlaylists,
  reorderPlaylistItems,
  changePlaylistDetails,
  deletePlaylist,
};
